import { api } from "@/api/client";
import type { CharOption, ResolveRequest } from "@/api/order_check";

/** Инвентаризация склада: смена, снимок учётных остатков, сканы, документ 1С. */

export interface RefName {
  ref: string;
  name: string;
}

export interface InventoryPosition {
  nomenclature_ref: string;
  nomenclature_name: string;
  nomenclature_article: string;
  characteristic_ref: string;
  characteristic_name: string;
  partia_ref: string;
  partia_name: string;
  /** Учёт на момент старта смены. */
  qty_accounted: number;
  qty_scanned: number;
  /** факт − учёт */
  deviation: number;
  /** Сколько ещё числится по учёту; отрицательное — уже перебор. */
  remaining: number;
  unit_name: string;
  /** Позиции нет в снимке: на складе лежит то, чего в учёте не было. */
  unexpected: boolean;
  /** Позицию ТРОГАЛИ. Ноль сканов ≠ «посчитали и там ноль». */
  counted: boolean;
  scans: number;
  last_at: string | null;
}

export interface InventoryProgress {
  positions_total: number;
  positions_counted: number;
  positions_matching: number;
  surplus_positions: number;
  shortage_positions: number;
  /** Позиции снимка, которых не касались — это НЕ недостача. */
  positions_untouched: number;
  unexpected_positions: number;
  scans_total: number;
  participants: string[];
}

export interface InventoryDetail {
  id: string;
  status: "open" | "completed";
  organization_ref: string;
  organization_name: string;
  warehouse_ref: string;
  warehouse_name: string;
  comment: string;
  started_at: string | null;
  started_by: string;
  snapshot_at: string | null;
  completed_at: string | null;
  one_c_doc_ref: string | null;
  one_c_doc_number: string | null;
  progress: InventoryProgress;
  positions: InventoryPosition[];
}

export interface InventorySummary {
  id: string;
  status: "open" | "completed";
  organization_name: string;
  warehouse_name: string;
  started_at: string | null;
  completed_at: string | null;
  snapshot_at: string | null;
  one_c_doc_number: string | null;
  positions_total: number;
  positions_counted: number;
  scans_total: number;
}

export interface InventoryScanRow {
  id: string;
  username: string;
  device: string;
  nomenclature_ref: string;
  nomenclature_name: string;
  characteristic_name: string;
  partia_name: string;
  scan_kind: string;
  raw_code: string;
  qty_scanned: number;
  unit_coef: number;
  qty_base: number;
  at: string | null;
}

export interface InventoryScanResult {
  ok: boolean;
  matched: boolean;
  /** Информационный текст ответа. ⚠️ НЕ предупреждение: успешный скан коробки
   *  несёт здесь «засчитано позиций — N» на КАЖДЫЙ скан, и жёлтая плашка по
   *  непустому `message` красила обычную работу в тревогу. */
  message: string;
  /** Предупреждение правила склада — ТОЛЬКО по нему красим баннер жёлтым
   *  (`warn` не гаснет сам, в отличие от `ok`). Ставится бэком в двух местах
   *  правила: успешный скан с отброшенной единственной характеристикой и
   *  вопрос, в котором отброшены ВСЕ варианты. */
  warning: string;
  needs_characteristic: boolean;
  needs_partia: boolean;
  available_characteristics: CharOption[];
  /** Варианты, отброшенные правилом склада «что не предлагать при скане».
   *  Приходят ОТДЕЛЬНО, а не выбрасываются: кнопка «изменить» в карточке
   *  скана обязана показать их тоже, иначе правило стало бы запретом и
   *  зафиксировать законную находку было бы нечем. */
  hidden_characteristics: CharOption[];
  /** Характеристику подставил ПОРТАЛ: правило склада оставило один вариант.
   *  Молча так делать нельзя — карточка скана говорит об этом и даёт
   *  «изменить». */
  characteristic_autofilled: boolean;
  /** Что опознали — шторка вопроса должна называть товар. */
  nomenclature_name: string;
  characteristic_name: string;
  /** Введённый номер партии, которого не нашлось в 1С (пусто — ещё не вводили). */
  partia_name: string;
  scan_id: string | null;
  /** Количество ЭТОГО скана (коробка/GS1 AI 30 — не 1); плашка показывает его
   *  без лишнего запроса `GET listScans`. */
  scan_qty?: number | null;
  position: InventoryPosition | null;
  progress: InventoryProgress | null;
}

export interface InventoryWorker {
  username: string;
  scans: number;
  last_at: string | null;
}

export async function listInventories(status?: string): Promise<InventorySummary[]> {
  const { data } = await api.get<InventorySummary[]>("/inventory/sessions", {
    params: status ? { status } : undefined,
  });
  return data;
}

export async function getInventory(id: string): Promise<InventoryDetail> {
  const { data } = await api.get<InventoryDetail>(`/inventory/sessions/${id}`);
  return data;
}

export async function startInventory(body: {
  organization_ref: string;
  organization_name: string;
  warehouse_ref: string;
  warehouse_name: string;
  comment?: string;
}): Promise<InventoryDetail> {
  const { data } = await api.post<InventoryDetail>("/inventory/sessions", body);
  return data;
}

export async function refreshSnapshot(id: string): Promise<InventoryDetail> {
  const { data } = await api.post<InventoryDetail>(`/inventory/sessions/${id}/snapshot`);
  return data;
}

/** Скан кода. Тело — общий `ResolveRequest` плюс метка устройства и уточнения. */
export async function scanInventory(
  id: string,
  body: ResolveRequest & {
    device?: string;
    characteristic_ref?: string;
    partia_ref?: string;
    /** Явный ответ «партии нет / не нашли» — иначе скан не принимается. */
    accept_without_partia?: boolean;
  },
): Promise<InventoryScanResult> {
  const { data } = await api.post<InventoryScanResult>(`/inventory/sessions/${id}/scan`, body);
  return data;
}

export async function manualCount(
  id: string,
  body: {
    nomenclature_ref: string;
    characteristic_ref?: string;
    partia_ref?: string;
    qty: number;
    device?: string;
  },
): Promise<InventoryScanResult> {
  const { data } = await api.post<InventoryScanResult>(`/inventory/sessions/${id}/manual`, body);
  return data;
}

export async function editScanQty(
  id: string,
  scanId: string,
  qty: number,
): Promise<InventoryScanResult> {
  const { data } = await api.patch<InventoryScanResult>(
    `/inventory/sessions/${id}/scans/${scanId}`,
    { qty },
  );
  return data;
}

/** Правило склада «что не предлагать при скане», как оно запомнено. */
export interface InventoryExcludedCharacteristics {
  warehouse_ref: string;
  /** Склад назван намеренно: правило переживает смену, и на экране должно
   *  быть видно, ДЛЯ КАКОГО склада оно запоминается. */
  warehouse_name: string;
  names: string[];
}

/** Имя характеристики для настройки правила. Имена, а не ссылки: «ОПТ» в
 *  справочнике 1С — пять живых элементов с разными `Ref_Key`. */
export interface InventoryCharacteristicName {
  name: string;
  /** Имя встречается в учётном снимке ЭТОГО склада — такие показываем сверху
   *  и предупреждаем: исключив, посчитать их обычным сканом не выйдет. */
  in_snapshot: boolean;
  excluded: boolean;
}

export async function getExcludedCharacteristics(
  id: string,
): Promise<InventoryExcludedCharacteristics> {
  const { data } = await api.get<InventoryExcludedCharacteristics>(
    `/inventory/sessions/${id}/excluded-characteristics`,
  );
  return data;
}

/** Запомнить правило ДЛЯ СКЛАДА. Пустой список снимает его. */
export async function saveExcludedCharacteristics(
  id: string,
  names: string[],
): Promise<InventoryExcludedCharacteristics> {
  const { data } = await api.put<InventoryExcludedCharacteristics>(
    `/inventory/sessions/${id}/excluded-characteristics`,
    { names },
  );
  return data;
}

export async function listCharacteristicNames(
  id: string,
): Promise<InventoryCharacteristicName[]> {
  const { data } = await api.get<InventoryCharacteristicName[]>(
    `/inventory/sessions/${id}/characteristic-names`,
  );
  return data;
}

/** Поправить характеристику у ЗАПИСАННОГО скана («изменить» в карточке).
 *
 *  ⚠️ Ссылку шлём КАК ЕСТЬ: `none` — это ОТВЕТ «без характеристики», его
 *  разворачивает сервер. Количество и автора ручка не трогает, позиция
 *  переезжает в другой ключ (ном, ХАР, партия). */
export async function editScanCharacteristic(
  id: string,
  scanId: string,
  body: { characteristic_ref: string; characteristic_name: string },
): Promise<InventoryScanResult> {
  const { data } = await api.patch<InventoryScanResult>(
    `/inventory/sessions/${id}/scans/${scanId}/characteristic`,
    body,
  );
  return data;
}

export async function deleteScan(id: string, scanId: string): Promise<void> {
  await api.delete(`/inventory/sessions/${id}/scans/${scanId}`);
}

export async function listScans(
  id: string,
  filter?: { nomenclature_ref?: string; characteristic_ref?: string; partia_ref?: string },
): Promise<InventoryScanRow[]> {
  const { data } = await api.get<InventoryScanRow[]>(`/inventory/sessions/${id}/scans`, {
    params: filter,
  });
  return data;
}

/** Лёгкая сводка для опроса по таймеру: без списка позиций. */
export async function getProgress(id: string): Promise<InventoryProgress> {
  const { data } = await api.get<InventoryProgress>(`/inventory/sessions/${id}/progress`);
  return data;
}

export async function listWorkers(id: string): Promise<InventoryWorker[]> {
  const { data } = await api.get<InventoryWorker[]>(`/inventory/sessions/${id}/workers`);
  return data;
}

export async function finishInventory(id: string): Promise<InventoryDetail> {
  const { data } = await api.post<InventoryDetail>(`/inventory/sessions/${id}/finish`);
  return data;
}

export async function reopenInventory(id: string): Promise<InventoryDetail> {
  const { data } = await api.post<InventoryDetail>(`/inventory/sessions/${id}/reopen`);
  return data;
}

export async function listInventoryOrganizations(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/inventory/organizations", { params: { q } });
  return data;
}

export async function listInventoryWarehouses(q?: string): Promise<RefName[]> {
  const { data } = await api.get<RefName[]>("/inventory/warehouses", { params: { q } });
  return data;
}
